// Office-specific adapter over the reusable NE login SDK (../../ne-sdk).
// All login logic + constants live in the SDK; here we only:
//   - namespace the localStorage key for this product, and
//   - provide the Office dialog opener for CARSI (host-specific bit).

import { LLMConfig } from "./types";
import {
  setNeStorageKey,
  loadNeAuth as sdkLoadNeAuth,
  saveNeAuth as sdkSaveNeAuth,
  clearNeAuth as sdkClearNeAuth,
  neAccountLogin as sdkAccountLogin,
  neCarsiLogin as sdkCarsiLogin,
  neFetchModels as sdkFetchModels,
  neConfig as sdkNeConfig,
  isQuotaError as sdkIsQuotaError,
  NeAuth as SdkNeAuth,
  NeUser as SdkNeUser,
  NeModelInfo as SdkNeModelInfo,
  NeModelsResult as SdkNeModelsResult,
  NeLoginResult,
} from "../../ne-sdk/src/client";
import { NE_BUILTIN_MODEL_NAME } from "../../ne-sdk/src/constants";

export type NeUser = SdkNeUser;
export type NeAuth = SdkNeAuth;
export type NeModelInfo = SdkNeModelInfo;
export type NeModelsResult = SdkNeModelsResult;
export { NE_BUILTIN_MODEL_NAME };

// Keep this product's existing storage namespace.
setNeStorageKey("horsir:ne:v1");

export const loadNeAuth = sdkLoadNeAuth;
export const saveNeAuth = sdkSaveNeAuth;
export const clearNeAuth = sdkClearNeAuth;
export const neAccountLogin = sdkAccountLogin;
export const neFetchModels = sdkFetchModels;
export const isQuotaError = sdkIsQuotaError;

/** Built-in model config. Uses auth.selectedModel when available, else the SDK default. */
export function neConfig(token: string, temperature: number, selectedModel?: string): LLMConfig {
  return sdkNeConfig(token, temperature, { model: selectedModel }) as LLMConfig;
}

// ---------------------------------------------------------------------------
// Credential storage for auto-refresh (keeps username + password in localStorage
// so the client can silently re-login when the JWT token is about to expire).
// ---------------------------------------------------------------------------
const CRED_KEY = "horsir:ne:cred:v1";

export interface NeCredentials {
  username: string;
  password: string;
}

export function saveNeCredentials(username: string, password: string): void {
  try {
    window.localStorage.setItem(CRED_KEY, JSON.stringify({ username, password }));
  } catch { /* ignore */ }
}

export function loadNeCredentials(): NeCredentials | null {
  try {
    const raw = window.localStorage.getItem(CRED_KEY);
    if (!raw) return null;
    const cred = JSON.parse(raw) as NeCredentials;
    if (!cred.username || !cred.password) return null;
    return cred;
  } catch { return null; }
}

export function clearNeCredentials(): void {
  try {
    window.localStorage.removeItem(CRED_KEY);
  } catch { /* ignore */ }
}

// ---------------------------------------------------------------------------
// JWT expiry helpers — best-effort base64 decode of the payload to read the
// `exp` claim. If the token isn't a JWT (e.g. opaque string), we skip the
// proactive check and rely on the existing reactive failure path.
// ---------------------------------------------------------------------------

/** Decode the payload segment of a JWT (header.payload.signature). Returns
 *  null if the token isn't a 3-part base64-encoded JWT. */
function decodeJwtPayload(token: string): Record<string, unknown> | null {
  try {
    const parts = token.split(".");
    if (parts.length !== 3) return null;
    // JWT uses base64url encoding (no padding)
    const normalized = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    const decoded = atob(normalized);
    return JSON.parse(decoded);
  } catch {
    return null;
  }
}

/**
 * Returns `true` when the NE token has no `exp` claim (can't verify), is
 * already expired, or will expire within `marginMs` (default 1 hour).
 *
 * When the token isn't a parseable JWT we return `false` (skip proactive
 * refresh) so the only observable behaviour is the existing reactive path:
 * the model-list fetch will fail and the UI shows the "重新登录" button.
 */
export function isNeTokenExpiring(auth: NeAuth, marginMs = 60 * 60 * 1000): boolean {
  const payload = decodeJwtPayload(auth.token);
  if (!payload) return false;              // not a JWT → can't check
  const exp = payload.exp as number | undefined;
  if (typeof exp !== "number") return false; // no expiry claim
  const nowMs = Date.now();
  return nowMs >= (exp * 1000 - marginMs);
}

/**
 * Try to refresh the NE token using saved credentials. When successful, the
 * new NeAuth is persisted to localStorage and returned. Returns `null` when
 * there are no saved credentials or the login fails (caller should keep the
 * old auth and show the normal login UI).
 */
export async function tryRefreshNeAuth(auth: NeAuth): Promise<NeAuth | null> {
  const cred = loadNeCredentials();
  if (!cred) return null;

  const result = await neAccountLogin(cred.username, cred.password);
  if (!result.ok || !result.token || !result.user) return null;

  const refreshed: NeAuth = {
    token: result.token,
    user: result.user,
    useBuiltin: auth.useBuiltin,
    selectedModel: auth.selectedModel,
  };
  saveNeAuth(refreshed);
  return refreshed;
}

/** CARSI login using an Office dialog as the opener; SDK does the rest. */
export function neCarsiLogin(): Promise<NeLoginResult> {
  const OfficeAny = (window as unknown as { Office?: any }).Office;
  const opener = (url: string): Promise<string> =>
    new Promise((resolve, reject) => {
      if (!OfficeAny?.context?.ui?.displayDialogAsync) {
        reject(new Error("当前环境不支持 CARSI 弹窗登录"));
        return;
      }
      OfficeAny.context.ui.displayDialogAsync(
        url,
        { height: 60, width: 40, promptBeforeOpen: false },
        (result: any) => {
          if (result.status !== "succeeded") {
            reject(new Error("无法打开登录窗口"));
            return;
          }
          const dialog = result.value;
          dialog.addEventHandler(OfficeAny.EventType.DialogMessageReceived, (arg: any) => {
            dialog.close();
            resolve(arg.message);
          });
          dialog.addEventHandler(OfficeAny.EventType.DialogEventReceived, () => {
            reject(new Error("登录窗口已关闭"));
          });
        }
      );
    });
  return sdkCarsiLogin(opener);
}
